import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, ShieldCheck, Lock } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

export default function RolesPage() {
  const { can, refreshMe } = useAuth();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({ modules: [], action_labels: {}, data_scopes: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", permissions: [], data_scope: "all" });
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const manage = can("roles:manage");

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get("/roles"), api.get("/permissions/catalog")]);
      setRoles(r.data);
      setCatalog(c.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "", permissions: ["dashboard:view"], data_scope: "all" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description || "", permissions: [...r.permissions], data_scope: r.data_scope || "all" }); setOpen(true); };

  const togglePerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
    }));
  };

  const toggleModule = (mod, checked) => {
    const keys = mod.actions.map((a) => `${mod.key}:${a}`);
    setForm((f) => ({
      ...f,
      permissions: checked ? Array.from(new Set([...f.permissions, ...keys])) : f.permissions.filter((p) => !keys.includes(p)),
    }));
  };

  const isAdminRole = editing?.is_admin;

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/roles/${editing.id}`, form);
        toast.success("Rol actualizado");
        await refreshMe();
      } else {
        await api.post("/roles", form);
        toast.success("Rol creado");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/roles/${delTarget.id}`);
      toast.success("Rol eliminado");
      setDelTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Define qué puede hacer cada rol en cada módulo del sistema.</p>
        {manage && (
          <Button onClick={openCreate} data-testid="new-role-btn" className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs">
            <Plus className="h-4 w-4" /> Nuevo rol
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((r) => (
            <Card key={r.id} className="rounded-sm border shadow-none p-4 flex flex-col" data-testid={`role-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 flex items-center justify-center bg-primary/10 text-primary rounded-sm">
                    {r.is_admin ? <Lock className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                  </div>
                  <div>
                    <p className="font-heading font-bold leading-tight">{r.name}</p>
                    <p className="text-[11px] text-muted-foreground">{r.user_count} usuario(s)</p>
                  </div>
                </div>
                {r.system && <span className="text-[10px] uppercase tracking-wider bg-muted px-1.5 py-0.5 rounded-sm text-muted-foreground">Sistema</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-3 flex-1">{r.description}</p>
              <div className="mt-3 text-[11px] font-mono text-muted-foreground">
                {r.is_admin ? "Acceso total" : `${r.permissions.length} permiso(s)`}
              </div>
              <div className="flex gap-2 mt-4">
                {manage && (
                  <Button variant="outline" size="sm" className="rounded-sm flex-1 gap-1.5" onClick={() => openEdit(r)} data-testid={`edit-role-${r.id}`}>
                    <Pencil className="h-3.5 w-3.5" /> {r.is_admin ? "Ver" : "Editar"}
                  </Button>
                )}
                {manage && !r.system && (
                  <Button variant="outline" size="sm" className="rounded-sm text-destructive" onClick={() => setDelTarget(r)} data-testid={`delete-role-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Matrix dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `Rol: ${editing.name}` : "Nuevo rol"}</DialogTitle>
            <DialogDescription>Marca los permisos que tendrá este rol en cada módulo.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Nombre del rol</Label>
              <Input data-testid="role-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-widest">Descripción</Label>
              <Input data-testid="role-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest">Alcance de datos</Label>
              <Select value={form.data_scope} onValueChange={(v) => setForm({ ...form, data_scope: v })} disabled={isAdminRole}>
                <SelectTrigger className="rounded-sm" data-testid="role-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(catalog.data_scopes || []).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Define qué registros ve este rol: solo propios, del equipo o todos.</p>
            </div>
          </div>

          <div className="mt-2">
            <Label className="text-xs font-bold uppercase tracking-widest">Permisos por módulo</Label>
            {isAdminRole && <p className="text-xs text-primary mt-1">Este rol tiene acceso total y no puede modificarse.</p>}
            <div className="mt-3 border border-border rounded-sm divide-y divide-border max-h-[45vh] overflow-y-auto">
              {Object.entries((catalog.modules || []).reduce((acc, m) => { (acc[m.group] = acc[m.group] || []).push(m); return acc; }, {})).map(([groupName, mods]) => (
                <div key={groupName} data-testid={`permgroup-${groupName}`}>
                  <div className="bg-muted px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">{groupName}</div>
                  {mods.map((mod) => {
                    const keys = mod.actions.map((a) => `${mod.key}:${a}`);
                    const allOn = keys.every((k) => form.permissions.includes(k));
                    return (
                      <div key={mod.key} className="p-3 border-t border-border" data-testid={`module-${mod.key}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Checkbox checked={allOn} disabled={isAdminRole} onCheckedChange={(v) => toggleModule(mod, !!v)} data-testid={`module-all-${mod.key}`} />
                          <span className="font-semibold text-sm">{mod.label}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-2 pl-6">
                          {mod.actions.map((a) => {
                            const key = `${mod.key}:${a}`;
                            return (
                              <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                                <Checkbox
                                  checked={form.permissions.includes(key)}
                                  disabled={isAdminRole}
                                  onCheckedChange={() => togglePerm(key)}
                                  data-testid={`perm-${key}`}
                                />
                                <span className="text-sm">{catalog.action_labels[a] || a}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cerrar</Button>
            {!isAdminRole && (
              <Button className="rounded-sm font-bold" onClick={save} disabled={saving || !form.name} data-testid="save-role-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar rol?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará el rol <strong>{delTarget?.name}</strong>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doDelete} data-testid="confirm-delete-role">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
